

import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { SMELessonService } from '../../../services/sme-lesson.service';
import { AuthService } from '../../../services/auth.service';
import { CommonApiService } from '@app/services/common-api.service';
import { unwrapLessonRow } from '@app/utils/vio-api-response';
import { TOCService } from '@app/services/toc.service';
import { extractApiErrorMessage } from '@app/utils/ai-error-prompt';
import { LessonExcelImportService } from '@app/services/lesson-excel-import.service';

@Component({
  selector: 'app-lesson-meta',
  templateUrl: './lesson-meta.component.html',
  styleUrls: ['./lesson-meta.component.scss']
})
export class LessonMetaComponent implements OnInit {

  lessonForm!: FormGroup;
  lessonId?: number;
  lesson: any;
  lessonStatus = 'DRAFT';
  lessonVersion = '1.0';
  isEdit = false;
  userId = this.commonAPI.userInfo.userID; // Replace with actual user ID retrieval logic

  generalexpectationfields = [
    { key: 'lessonCaseStudy', label: 'Include Case Studies' },
    { key: 'lessonExamples', label: 'Include Examples' },
    { key: 'lessonAssessment', label: 'Include Assessment' },
    { key: 'lessonSectionTOC', label: 'Use Sections in TOC' }
  ];

  technicalexpectationfields = [
    { key: 'lessonDerivations', label: 'Include Formula Derivations' },
    { key: 'lessonGoodPractices', label: 'Include Good Practices' },
    { key: 'lessonSafetyInstr', label: 'Include Safety Instructions' },
    { key: 'lessonIncludeStandards', label: 'Include Standards' }
  ];

  lessonLearnerProfile = [
    'New joinee',
    'Experienced professional',
    'Experienced but new to subject',
    'Mixed'
  ];

  lessonStyle = [
    'Simple',
    'Casual',
    'Serious',
    'Dialoguing'
  ];

  lessonType = ['technical', 'management', 'software', 'others'];

  ynnaOptions = ['Y', 'N', 'NA'];

  /** SME reference files for AI grounding (PDF/images) — not slide assets */
  references: any[] = [];
  refUploadError = '';
  refUploading = false;
  refRefreshOpenAiBusy = false;
  /** Y = primary reference (strict lesson from file); N = supplemental grounding. */
  refUploadPrimary: 'Y' | 'N' = 'N';
  refUploadStatus = '';
  linkedToProject = false;
  contentActionBusy = false;
  licenseStatus: { ok?: boolean; message?: string; available?: number; total?: number } | null = null;
  readonly refAllowedMimeTypes = [
    'application/pdf',
    'image/png',
    'image/jpeg',
    'image/gif',
    'image/webp',
  ];
  readonly refMaxBytes = 25 * 1024 * 1024;
  readonly refMaxMbLabel = '25 MB';

  assetsZipUploading = false;
  assetsZipDownloading = false;
  assetsZipFileName = '';
  assetsZipStatus = '';
  assetsZipError = '';
  private pendingAssetsZipBase64 = '';
  private pendingAssetsZipName = '';

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private lessonService: SMELessonService,
    private authService: AuthService,
    private commonAPI: CommonApiService,
    private tocService: TOCService,
    private lessonAssetsZipSvc: LessonExcelImportService,
  ) { }

  ngOnInit(): void {
    console.log('LessonMetaComponent initialized');
    this.lessonId = Number(this.route.snapshot.paramMap.get('lessonid'));
    this.userId = this.resolveUserId();
    this.isEdit = !!this.lessonId;

    this.initForm();

    if (this.isEdit) {
      this.loadLesson();
    } else {
      this.refreshLicenseStatus();
    }
  }

  refreshLicenseStatus(): void {
    if (!Number.isFinite(this.userId)) return;
    this.lessonService.getLicenseAvailability(this.userId).subscribe({
      next: (status) => {
        this.licenseStatus = status;
      },
      error: () => {
        this.licenseStatus = { ok: false, message: 'Could not verify license availability.' };
      },
    });
  }

  canCreateLesson(): boolean {
    return this.licenseStatus?.ok !== false;
  }

  private resolveUserId(): number {
    const fromAuth = this.authService.getUser();
    if (typeof fromAuth === 'number' && Number.isFinite(fromAuth)) return fromAuth;
    if (fromAuth && typeof fromAuth === 'object') {
      const n = Number((fromAuth as any).userID ?? (fromAuth as any).userId);
      if (Number.isFinite(n)) return n;
    }
    const n = Number(this.commonAPI.userInfo?.userID);
    return Number.isFinite(n) ? n : NaN;
  }

  initForm() {
    //srNo, userId, lessonId, lessonTitle, lessonInstructions, lessonSubject, lessonType, lessonVersion,
    // lessonStatus, lessonSlideCount, lessonLearnerProfile, lessonStyle, lessonSectionTOC,
    // lessonCaseStudy, lessonExamples, lessonAssessment,
    //  lessonDerivations, LessonGoodPractices, lessonSafetyInstr
    this.lessonForm = this.fb.group({
      lessonTitle: ['', Validators.required],
      lessonInstructions: [''],
      lessonSubject: [''],
      lessonType: ['technical'],
      lessonContentMode: ['generic'],
      lessonVersion: ['1.0'],
      lessonStatus: ['EMPTY'],


      lessonSlideCount: ['10', Validators.required],
      lessonLearnerProfile: ['New joinee'],
      lessonStyle: [[]],
      lessonDeliveredInPerson: ['N'],


      preferences: this.fb.group({

        lessonCaseStudy: ['NA'],
        lessonExamples: ['Y'],
        lessonAssessment: ['NA'],
        lessonSectionTOC: ['Y'],
        lessonQuizPerSection: [0],
        lessonQuizEndCount: [0],
        lessonQuizSimpleCount: [0],
        lessonQuizComplexCount: [0],
        lessonQuizAppliedFinalCount: [0],
      }),
      techpreferences: this.fb.group({
        lessonDerivations: ['NA'],
        lessonGoodPractices: ['NA'],
        lessonSafetyInstr: ['NA'],
        lessonIncludeStandards: ['NA']
      })
    });
  }

  loadLesson() {
    this.lessonService.getLessonMeta(this.userId, this.lessonId!)
      .subscribe({
        next: (res) => {
          this.lesson = unwrapLessonRow(res);
          if (!this.lesson) {
            console.error('Lesson meta: empty response');
            return;
          }
          console.log('Loaded lesson:', this.lesson);
          this.lessonStatus = this.lesson.lessonStatus || 'EMPTY';
          this.lessonVersion = this.lesson.lessonVersion;
          this.linkedToProject = !!(this.lesson.linkedToProject);
          this.lessonForm.patchValue(this.lesson);

          if (this.lessonStatus !== 'EMPTY') {
            this.lessonForm.disable();
          }
          this.loadReferences();
        },
        error: (err) => {
          console.error('Failed to load lesson meta', err);
          alert('Could not load lesson details.');
        },
      });
  }

  hasToc(): boolean {
    const st = String(this.lessonStatus || '').toUpperCase();
    return st !== '' && st !== 'EMPTY';
  }

  canClearLessonContent(): boolean {
    return this.isEdit && !this.linkedToProject && this.hasToc();
  }

  clearLessonContent(): void {
    if (!this.lessonId || this.linkedToProject) return;
    if (
      !window.confirm(
        'Clear lesson content? The lesson properties are kept, but TOC, slides, project slides, generated images/audio, references, and PPT files are removed.',
      )
    ) {
      return;
    }
    this.contentActionBusy = true;
    this.tocService.clearLessonContent(this.userId, this.lessonId).subscribe({
      next: () => {
        this.contentActionBusy = false;
        this.lessonStatus = 'EMPTY';
        this.lessonForm.enable();
        this.proceedToTOC();
      },
      error: (err: unknown) => {
        this.contentActionBusy = false;
        alert(extractApiErrorMessage(err, 'Failed to clear lesson content.'));
      },
    });
  }

  deleteLesson(): void {
    if (!this.lessonId || this.linkedToProject) return;
    if (
      !window.confirm(
        'Delete this lesson permanently? This removes the lesson and all generated content. This cannot be undone.',
      )
    ) {
      return;
    }
    this.contentActionBusy = true;
    this.lessonService.deleteLesson(this.userId, this.lessonId).subscribe({
      next: () => {
        this.contentActionBusy = false;
        this.router.navigate(['/SME/dashboard']);
      },
      error: (err: unknown) => {
        this.contentActionBusy = false;
        alert(extractApiErrorMessage(err, 'Failed to delete lesson.'));
      },
    });
  }

  loadReferences() {
    if (!this.isEdit || !this.lessonId) return;
    this.lessonService.listLessonReferences(this.userId, this.lessonId).subscribe({
      next: (rows) => {
        this.references = Array.isArray(rows) ? rows : [];
      },
      error: (err: unknown) => {
        this.references = [];
        this.refUploadError = extractApiErrorMessage(err, 'Could not load reference file list.');
      },
    });
  }

  canManageReferences(): boolean {
    return this.isEdit && !!this.lessonId;
  }

  referencesLocked(): boolean {
    return this.lessonStatus !== 'EMPTY' && this.lessonStatus !== 'TOC';
  }

  onReferenceFilesSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files || []);
    input.value = '';
    if (!files.length || !this.lessonId) return;
    void this.uploadReferenceFiles(files);
  }

  isPrimaryReference(ref: { referenceRole?: string } | null | undefined): boolean {
    return String(ref?.referenceRole || 'supplemental').toLowerCase() === 'primary';
  }

  primaryReferenceYn(ref: { referenceRole?: string } | null | undefined): 'Y' | 'N' {
    return this.isPrimaryReference(ref) ? 'Y' : 'N';
  }

  setPrimaryReference(ref: any, value: 'Y' | 'N') {
    const role = value === 'Y' ? 'primary' : 'supplemental';
    this.setReferenceRole(ref, role);
  }

  private readFileAsDataUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(new Error(`Could not read ${file.name}`));
      reader.readAsDataURL(file);
    });
  }

  private async uploadReferenceFiles(files: File[]) {
    this.refUploadError = '';
    const errors: string[] = [];
    const valid: File[] = [];

    for (const file of files) {
      if (!this.refAllowedMimeTypes.includes(file.type)) {
        errors.push(`${file.name}: allowed types are PDF, PNG, JPEG, GIF, WebP`);
        continue;
      }
      if (file.size > this.refMaxBytes) {
        errors.push(`${file.name}: exceeds ${this.refMaxMbLabel} limit`);
        continue;
      }
      valid.push(file);
    }

    if (!valid.length) {
      this.refUploadError = errors.join('\n');
      return;
    }

    this.refUploading = true;
    let uploaded = 0;
    for (const file of valid) {
      this.refUploadStatus = `Uploading ${uploaded + 1} of ${valid.length}: ${file.name}`;
      try {
        const dataUrl = await this.readFileAsDataUrl(file);
        await new Promise<void>((resolve, reject) => {
          this.lessonService
            .uploadLessonReference(this.userId, this.lessonId!, file.name, dataUrl, {
              label: file.name,
              mimeType: file.type,
              referenceRole: this.refUploadPrimary === 'Y' ? 'primary' : 'supplemental',
            })
            .subscribe({
              next: () => resolve(),
              error: (err: unknown) => {
                const e = err as { error?: { data?: { message?: string }; message?: string } };
                reject(new Error(e?.error?.data?.message || e?.error?.message || `Upload failed: ${file.name}`));
              },
            });
        });
        uploaded += 1;
      } catch (err: unknown) {
        errors.push(err instanceof Error ? err.message : String(err));
      }
    }

    this.refUploading = false;
    this.refUploadStatus = '';
    if (uploaded > 0 && !errors.length) {
      this.refUploadStatus = `Uploaded ${uploaded} file(s).`;
    } else if (uploaded > 0) {
      this.refUploadStatus = `Uploaded ${uploaded} of ${valid.length} file(s).`;
    }
    this.loadReferences();
    if (errors.length) {
      this.refUploadError = errors.join('\n');
    }
  }

  deleteReference(refId: string) {
    if (!this.lessonId || !confirm('Remove this reference file?')) return;
    this.lessonService.deleteLessonReference(this.userId, this.lessonId, refId).subscribe({
      next: () => this.loadReferences(),
      error: () => alert('Could not delete reference'),
    });
  }

  toggleReferenceFlag(ref: any, field: 'useForToc' | 'useForSlides', event: Event) {
    const checked = (event.target as HTMLInputElement).checked;
    const value: 'Y' | 'N' = checked ? 'Y' : 'N';
    if (!this.lessonId) return;
    this.lessonService
      .updateLessonReference(this.userId, this.lessonId, ref.refId, { [field]: value })
      .subscribe({
        next: () => {
          ref[field] = value;
        },
        error: () => {
          (event.target as HTMLInputElement).checked = !checked;
          alert('Could not update reference');
        },
      });
  }

  setReferenceRole(ref: any, role: 'supplemental' | 'primary') {
    if (!this.lessonId || ref.referenceRole === role) return;
    this.lessonService
      .updateLessonReference(this.userId, this.lessonId, ref.refId, { referenceRole: role })
      .subscribe({
        next: () => {
          ref.referenceRole = role;
        },
        error: () => alert('Could not update reference role'),
      });
  }

  formatRefSize(bytes: number): string {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  isReferenceOpenAiExpired(ref: {
    openAiFileExpiredFlag?: boolean | number | string;
    openAiFileExpired?: boolean | number | string;
  } | null | undefined): boolean {
    if (!ref) return false;
    const flag = ref.openAiFileExpiredFlag ?? ref.openAiFileExpired;
    return flag === true || flag === 1 || flag === '1' || String(flag).toLowerCase() === 'true';
  }

  hasExpiredOpenAiReferences(): boolean {
    return (this.references || []).some((r) => this.isReferenceOpenAiExpired(r));
  }

  expiredOpenAiReferenceNames(): string {
    return (this.references || [])
      .filter((r) => this.isReferenceOpenAiExpired(r))
      .map((r) => String(r?.label || r?.fileName || r?.refId || 'file').trim())
      .filter(Boolean)
      .join(', ');
  }

  refreshExpiredOpenAiUploads(): void {
    if (!this.lessonId || this.refRefreshOpenAiBusy) return;
    this.refRefreshOpenAiBusy = true;
    this.refUploadError = '';
    this.refUploadStatus = 'Refreshing OpenAI uploads…';
    this.lessonService.refreshLessonReferenceOpenAi(this.userId, this.lessonId).subscribe({
      next: (res: unknown) => {
        this.refRefreshOpenAiBusy = false;
        const data =
          (res as { Data?: { references?: unknown[]; refreshed?: number } })?.Data ??
          (res as { data?: { references?: unknown[]; refreshed?: number } })?.data ??
          res;
        const rows = (data as { references?: unknown[] })?.references;
        if (Array.isArray(rows)) {
          this.references = rows;
        } else {
          this.loadReferences();
        }
        const n = Number((data as { refreshed?: number })?.refreshed);
        this.refUploadStatus =
          Number.isFinite(n) && n > 0
            ? `Refreshed ${n} OpenAI file upload(s). Generate TOC / slides from chat when ready.`
            : 'OpenAI uploads are up to date.';
      },
      error: (err: unknown) => {
        this.refRefreshOpenAiBusy = false;
        this.refUploadStatus = '';
        this.refUploadError = extractApiErrorMessage(err, 'Could not refresh OpenAI uploads.');
      },
    });
  }

  onLessonAssetsZipSelected(ev: Event): void {
    const input = ev.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file || !this.lessonId) return;
    this.assetsZipFileName = file.name;
    this.assetsZipError = '';
    this.assetsZipStatus = '';
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = String(reader.result || '');
      this.pendingAssetsZipBase64 = dataUrl.includes(',') ? dataUrl.split(',')[1] : dataUrl;
      this.pendingAssetsZipName = file.name;
      this.sendLessonAssetsZip(false);
    };
    reader.onerror = () => {
      this.assetsZipError = 'Could not read ZIP file.';
    };
    reader.readAsDataURL(file);
  }

  private sendLessonAssetsZip(overwrite: boolean): void {
    if (!this.lessonId || !this.pendingAssetsZipBase64) return;
    this.assetsZipUploading = true;
    this.assetsZipError = '';
    this.lessonAssetsZipSvc
      .uploadImageZip(Number(this.lessonId), this.pendingAssetsZipName, this.pendingAssetsZipBase64, overwrite)
      .subscribe({
        next: (data) => {
          this.assetsZipUploading = false;
          this.pendingAssetsZipBase64 = '';
          const n = data?.totalWritten ?? 0;
          const ow = (data?.overwritten || []).length;
          this.assetsZipStatus = ow
            ? `Extracted ${n} image(s); ${ow} overwritten under lesson folder.`
            : `Extracted ${n} image(s) under lesson folder.`;
        },
        error: (err) => {
          this.assetsZipUploading = false;
          const body = err?.error || {};
          if (body?.code === 'NEEDS_OVERWRITE' || err?.status === 409) {
            const count = body.conflictCount || (body.conflicts || []).length;
            const ok = confirm(
              `${count} image file(s) already exist for this lesson.\n\nOverwrite existing files?`,
            );
            if (ok) this.sendLessonAssetsZip(true);
            else this.assetsZipStatus = 'ZIP upload cancelled — existing files kept.';
            return;
          }
          const invalid = (body.invalidPaths || []).slice(0, 5).join('\n');
          this.assetsZipError =
            extractApiErrorMessage(err, 'ZIP upload failed') + (invalid ? `\n${invalid}` : '');
        },
      });
  }

  downloadLessonAssetsZip(): void {
    if (!this.lessonId || this.assetsZipDownloading) return;
    this.assetsZipDownloading = true;
    this.assetsZipError = '';
    this.assetsZipStatus = 'Preparing download…';
    this.lessonAssetsZipSvc.downloadImageZip(Number(this.lessonId)).subscribe({
      next: (blob) => {
        this.assetsZipDownloading = false;
        if (!blob || blob.size === 0) {
          this.assetsZipError = 'Download was empty — no slide images on the server yet.';
          this.assetsZipStatus = '';
          return;
        }
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `lesson_${this.lessonId}_images.zip`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        this.assetsZipStatus = 'Slide images ZIP downloaded (layout: slideId/file).';
      },
      error: (err) => {
        this.assetsZipDownloading = false;
        this.assetsZipStatus = '';
        this.assetsZipError = extractApiErrorMessage(
          err,
          'Download failed. Processing may not have completed — try again.',
        );
      },
    });
  }

  isPdfRef(ref: any): boolean {
    return String(ref?.mimeType || '').includes('pdf');
  }

  isTechnical(): boolean {
    return this.lessonForm.get('lessonType')?.value === 'technical';
  }

  isAssessmentEnabled(): boolean {
    return this.lessonForm.get('preferences.lessonAssessment')?.value === 'Y';
  }

  save() {
    if (this.lessonForm.invalid) return;

    const payload = this.lessonForm.value;
    console.log('Payload to save:', payload);

    if (this.isEdit) {
      this.lessonService.updateLessonMeta(this.userId, this.lessonId!, payload)
        .subscribe(() => alert('Saved'));
    } else {
      if (!this.canCreateLesson()) {
        alert(this.licenseStatus?.message || 'No licenses available. Contact your administrator.');
        return;
      }
      this.lessonService.createLesson(this.userId, payload)
        .subscribe({
          next: () => {
            alert('Lesson created successfully');
            this.router.navigate(['/SME/dashboard']);
          },
          error: (err) => {
            const msg = err?.error?.error || err?.error?.message || 'Failed to create lesson';
            alert(msg);
            this.refreshLicenseStatus();
          },
        });
    }
  }

  proceedToTOC() {
    if (!this.lessonId) return;

    this.router.navigate(['/SME/lesson/toc/lessonid', this.lessonId]);
  }
  backToDashboard() {
    this.router.navigate(['/SME/dashboard']);
  }
}
